// Industry data: each with use cases and user stories
export const industries = [
  {
    slug: "manufacturing",
    name: "Manufacturing & Supply Chain",
    tagline: "Autonomous factories. Predictive supply networks.",
    summary:
      "We help discrete and process manufacturers modernise operations with computer vision, predictive maintenance and AI-driven planning — from the shop floor to the tier-N supplier.",
    image:
      "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxoaWdoJTIwdGVjaCUyMGZhY3RvcnklMjBhdXRvbWF0aW9ufGVufDB8fHx8MTc4MzY3NTkzMnww&ixlib=rb-4.1.0&q=85",
    metrics: [
      { value: "-38%", label: "Unplanned downtime" },
      { value: "+22%", label: "OEE uplift" },
      { value: "12wks", label: "Time to first value" },
    ],
    useCases: [
      {
        title: "Predictive Maintenance",
        body: "Time-series models on vibration, thermal and current signals detect anomalies days before failure, cutting unplanned downtime and MRO costs.",
      },
      {
        title: "Vision QC on the Line",
        body: "Edge-deployed CNNs inspect surface defects, weld quality and packaging integrity at line-speed with >99% accuracy.",
      },
      {
        title: "Demand & S&OP Copilot",
        body: "LLM copilots reconcile ERP, POS and market signals into probabilistic demand plans and what-if scenarios for planners.",
      },
      {
        title: "Digital Thread & Traceability",
        body: "Unified data fabric linking BOM, MES and QMS — instantly trace any defect back to raw batch and supplier.",
      },
    ],
    userStories: [
      {
        persona: "Ravi, Plant Head — auto components",
        story:
          "As a plant head, I want to know which CNC spindle will fail this week so I can schedule maintenance during a planned changeover — instead of losing a full shift to a surprise breakdown.",
      },
      {
        persona: "Meera, Supply Chain Director",
        story:
          "As an SC director, I want a live risk view across my tier-2 suppliers so I can pre-position inventory before a monsoon disruption hits.",
      },
      {
        persona: "Anil, Quality Lead",
        story:
          "As a QC lead, I want vision AI to catch micro-scratches my line inspectors miss, so warranty claims from OEMs drop this quarter.",
      },
    ],
  },
  {
    slug: "pharma-biotech",
    name: "Pharma & Biotech",
    tagline: "Compress discovery. Accelerate compliance.",
    summary:
      "From molecule to market — we build AI systems for target discovery, clinical operations, regulatory intelligence and GxP-compliant manufacturing analytics.",
    image:
      "https://images.pexels.com/photos/10514991/pexels-photo-10514991.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    metrics: [
      { value: "3.4x", label: "Faster literature review" },
      { value: "-45%", label: "Batch release cycle" },
      { value: "21 CFR", label: "Part 11 aligned" },
    ],
    useCases: [
      {
        title: "Target & Molecule Discovery",
        body: "Graph neural networks and generative chemistry accelerate hit identification and lead optimisation against novel targets.",
      },
      {
        title: "Clinical Trial Intelligence",
        body: "NLP over protocols, EHR and site data to optimise site selection, patient recruitment and adverse-event signal detection.",
      },
      {
        title: "Regulatory Copilot",
        body: "Retrieval-augmented agents draft CMC sections, respond to health-authority queries and monitor guideline changes across FDA / EMA / CDSCO.",
      },
      {
        title: "GxP Analytics & Batch Release",
        body: "Golden-batch models and validated audit trails cut batch review time while preserving Part 11 traceability.",
      },
    ],
    userStories: [
      {
        persona: "Dr. Nair, Discovery Lead",
        story:
          "As a discovery lead, I want an AI to shortlist 200 druggable analogues from a 10M compound library overnight, so my team focuses only on wet-lab work that matters.",
      },
      {
        persona: "Priya, Clinical Operations",
        story:
          "As clin-ops, I want to spot underperforming sites in week 6 rather than week 26, so I can re-allocate patient recruitment before we blow the timeline.",
      },
      {
        persona: "Vikram, Regulatory Affairs",
        story:
          "As a regulatory lead, I want a copilot that drafts a first-pass response to an FDA IR in an hour with cited source docs, so I ship submissions faster without risking accuracy.",
      },
    ],
  },
  {
    slug: "education",
    name: "Education & EdTech",
    tagline: "Personalised learning. Institutional intelligence.",
    summary:
      "We help schools, universities and edtech platforms deploy adaptive learning, assessment AI and operational analytics — grounded in pedagogy, not hype.",
    image:
      "https://images.unsplash.com/photo-1758685733664-4cde7bbe4713?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBlZHVjYXRpb24lMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc4MzY3NTkzMnww&ixlib=rb-4.1.0&q=85",
    metrics: [
      { value: "+31%", label: "Learner engagement" },
      { value: "-60%", label: "Grading time" },
      { value: "K-PG", label: "Coverage" },
    ],
    useCases: [
      {
        title: "Adaptive Learning Paths",
        body: "Bayesian knowledge tracing personalises pace, difficulty and modality per learner — with dashboards for teachers, not black boxes.",
      },
      {
        title: "AI Tutor & Doubt-Solving",
        body: "Curriculum-grounded LLM tutors answer with cited textbook passages and refuse to hallucinate beyond scope.",
      },
      {
        title: "Automated Assessment",
        body: "Rubric-aware scoring for MCQ, short-answer and essays with explainable feedback and plagiarism / AI-writing detection.",
      },
      {
        title: "Institutional Analytics",
        body: "Early-warning models flag at-risk students weeks earlier, and forecast enrolment, retention and placement outcomes.",
      },
    ],
    userStories: [
      {
        persona: "Ms. Iyer, Grade-8 Teacher",
        story:
          "As a teacher, I want a weekly report showing which of my 40 students are stuck on which sub-concept, so I can intervene before mid-terms — without spending my Sunday grading.",
      },
      {
        persona: "Karthik, Product Head at an EdTech",
        story:
          "As a PM, I want an in-app tutor that stays on-syllabus and never invents facts, so parents trust our product with their child's board exam prep.",
      },
      {
        persona: "Registrar, Tier-1 University",
        story:
          "As a registrar, I want to predict which first-years are likely to drop out by semester two, so my student-success team can reach them early.",
      },
    ],
  },
  {
    slug: "agritech",
    name: "Agritech",
    tagline: "Data on every acre. Insight to every farmer.",
    summary:
      "Satellite, drone and IoT signals fused with agronomic models — for FPOs, agri-input majors and food companies working across the value chain.",
    image:
      "https://images.pexels.com/photos/34182412/pexels-photo-34182412.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    metrics: [
      { value: "+18%", label: "Yield uplift" },
      { value: "-27%", label: "Input cost" },
      { value: "5M+", label: "Acres analysed" },
    ],
    useCases: [
      {
        title: "Yield & NDVI Forecasting",
        body: "Sentinel + Planet imagery combined with weather and soil layers produce plot-level yield forecasts weeks before harvest.",
      },
      {
        title: "Pest & Disease Early-Warning",
        body: "Vision models on smartphone images and canopy sensors detect blast, blight and bollworm outbreaks before they spread.",
      },
      {
        title: "Advisory & Voice Bots",
        body: "Vernacular voice agents deliver crop-stage advisories to farmers over IVR and WhatsApp — no smartphone literacy required.",
      },
      {
        title: "Supply Chain Traceability",
        body: "Farm-to-fork traceability with QR-linked batch data — critical for exports, ESG and residue compliance.",
      },
    ],
    userStories: [
      {
        persona: "Suresh, FPO Manager",
        story:
          "As an FPO manager, I want to tell my 1,200 farmers exactly when to spray and how much fertiliser to use this week, so we cut input costs and boost realisation at mandi.",
      },
      {
        persona: "Anjali, Agri-input Product Lead",
        story:
          "As a product lead, I want to know where late-blight risk will spike next week across 4 states, so I can pre-position stock and brief the field team.",
      },
      {
        persona: "Head of Sourcing, Food Brand",
        story:
          "As sourcing head, I want proof that every tonne of grain I bought is residue-compliant and traceable to plot, so my export shipments never get rejected.",
      },
    ],
  },
];

export const findIndustry = (slug) =>
  industries.find((i) => i.slug === slug) || null;
